<template>
  <div>
    <div
      class="meridia-text-area-wrapper"
      :class="[
        sizeClass,
        stateClass,
        getPt < PassThroughMeridiaTextArea > ('wrapper', passThrough)?.class,
      ]"
    >
      <MeridiaIcon
        v-if="icon && icon_position === TextAreaIconPosition.LEFT"
        class="icon"
        :class="
          getPt < PassThroughMeridiaTextArea > ('icon', passThrough)?.class
        "
        :name="icon"
        type="material-icons-outlined"
      />
      <PrimeTextArea
        v-model="modelValue"
        unstyled
        :pt="passThrough"
        :placeholder="placeholder"
        :disabled="state === TextAreaState.DISABLED"
        :auto-resize="autoResize"
        :rows="rows"
        :cols="cols"
        class="meridia-text-area"
        v-bind="$attrs"
      />
      <div class="w-4 h-4 flex justify-center items-center">
        <transition name="fade">
          <MeridiaIcon
            v-if="modelValue"
            class="icon-clear"
            :class="
              getPt <
              PassThroughMeridiaTextArea >
              ('clearIcon', passThrough)?.class
            "
            @click="clear"
            name="cancel"
            type="material-icons"
          />
        </transition>
      </div>
      <MeridiaIcon
        v-if="icon && icon_position === TextAreaIconPosition.RIGHT"
        class="icon"
        :class="
          getPt < PassThroughMeridiaTextArea > ('icon', passThrough)?.class
        "
        :name="icon"
        type="material-icons-outlined"
      />
    </div>
    <transition name="fade">
      <span
        v-if="helper_text"
        class="helper-text"
        :class="
          getPt <
          PassThroughMeridiaTextArea >
          ('helper_text', passThrough)?.class
        "
        >{{ helper_text }}</span
      >
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  TextAreaSize,
  TextAreaIconPosition,
  TextAreaState,
  PassThroughMeridiaTextArea,
} from './types';
import { usePassThrough } from 'primevue/passthrough';
import PrimeTextArea from 'primevue/textarea';
import { getPt } from '../utils';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

const modelValue = defineModel<string | undefined>({ required: true });

const props = withDefaults(
  defineProps<{
    size?: `${TextAreaSize}`;
    icon_position?: `${TextAreaIconPosition}`;
    placeholder?: string;
    helper_text?: string;
    state?: `${TextAreaState}`;
    icon?: string;
    autoResize?: boolean;
    rows?: number;
    cols?: number;
    pt?: PassThroughMeridiaTextArea;
  }>(),
  {
    size: TextAreaSize.SMALL,
    icon_position: TextAreaIconPosition.RIGHT,
  }
);

const emit = defineEmits<{
  (event: 'update:model-value', value: boolean): void;
  /**
   * fired when user clears the input
   */
  (event: 'clear'): void;
}>();

const sizeClass = computed(() => ({
  'size-small': props.size === TextAreaSize.SMALL,
  'size-large': props.size === TextAreaSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-error': props.state === TextAreaState.ERROR,
  'state-success': props.state === TextAreaState.SUCCESS,
  'state-disabled': props.state === TextAreaState.DISABLED,
  'state-typed': modelValue.value,
}));

const passThrough = usePassThrough(props.pt, {} as PassThroughMeridiaTextArea, {
  mergeSections: false,
  mergeProps: true,
}) as PassThroughMeridiaTextArea;

function clear() {
  if (props.state === TextAreaState.DISABLED) return;
  emit('clear');
  modelValue.value = '';
}
</script>

<style lang="scss" scoped>
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-out;
}
</style>

<style src="./style.scss" scoped lang="scss"></style>
